import { Document, Font } from "@react-pdf/renderer";
import React, { useMemo } from "react";
import robotoBold from "../../../fonts/roboto-700.ttf";
import roboto from "../../../fonts/roboto.ttf";
import { flattenObject } from "../../../lib/flattenObject";
import { overrideLabelMap } from "../../../lib/models/override-label-map";
import { SFUserProfile } from "../../../lib/models/sf-user-profile.model";
import { FixAndFlipCalculation } from "../../../lib/models/short-term/short-term-calculation.model";
import { FixAndFlipQuote } from "../../../lib/models/short-term/short-term.model";
import { Comment } from "../../../lib/types/CommentType";
import { PdfGenerationActions } from "../../../lib/types/PDFGenerationActions";
import { QuoteMetadata } from "../../../lib/types/QuoteMetadata";
import { bulletListToArray } from "../../../lib/utils";
import STFCalculationSheet from "./STFCalculationSheet";
import STFInternalSelectionSheet from "./STFInternalSelectionSheet";

export interface FixAndFlipTermSheetProps {
  quote: FixAndFlipQuote;
  calculation: FixAndFlipCalculation;
  action: PdfGenerationActions;
  userProfile: SFUserProfile;
  metadata: QuoteMetadata;
  discussionArray: Comment[] | undefined;
}

const FixAndFlipTermSheet = ({
  quote,
  calculation,
  action,
  userProfile,
  metadata,
  discussionArray,
}: FixAndFlipTermSheetProps) => {
  const { exceptionPricingOverride } = quote;
  const { rateLLPAsFactored, feeLLPAsFactored, overridePricing, warnings } =
    calculation;

  const rateLLPAsArray = useMemo(
    () => bulletListToArray(rateLLPAsFactored),
    [rateLLPAsFactored]
  );

  const feeLLPAsArray = useMemo(
    () => bulletListToArray(feeLLPAsFactored),
    [feeLLPAsFactored]
  );

  const warningsArray = useMemo(() => {
    return warnings ? bulletListToArray(warnings) : undefined;
  }, [warnings]);

  const overrideArray = useMemo(() => {
    const overrides = bulletListToArray(overridePricing);
    if (exceptionPricingOverride) {
      for (const [key, value] of Object.entries(exceptionPricingOverride)) {
        if (value !== undefined) {
          if (key === "exceptionType") {
            overrides.unshift(`${overrideLabelMap[key]}: ${value}`);
          }
        }
      }

      if (overrides.length) {
        overrides.unshift(
          `Exception Status: ${metadata.exceptionStatus ?? "Not Submitted"}`
        );
      }
      return overrides;
    }
  }, [overridePricing, metadata.exceptionStatus, exceptionPricingOverride]);

  const flattenedQuoteCalculation: { [key: string]: any } = useMemo(() => {
    calculation["brokerFeePercentage"] = quote.loanTerm?.brokerFee;
    return flattenObject({ quote, calculation }) || {};
  }, [quote, calculation]);

  const quoteDate = useMemo(() => {
    const date = new Date();
    return date.toLocaleDateString();
  }, []);

  return (
    <Document>
      <STFCalculationSheet
        action={action}
        flattenedQuoteCalculation={flattenedQuoteCalculation}
        quoteDate={quoteDate}
        userProfile={userProfile}
        metadata={metadata}
      />
      {action === "INTERNAL" && (
        <STFInternalSelectionSheet
          feeLLPAsArray={feeLLPAsArray}
          rateLLPAsArray={rateLLPAsArray}
          overrideArray={overrideArray}
          quoteDate={quoteDate}
          userProfile={userProfile}
          flattenedQuoteCalculation={flattenedQuoteCalculation}
          warningsArray={warningsArray}
          discussionArray={discussionArray}
        />
      )}
    </Document>
  );
};

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: roboto,
    },
    {
      src: robotoBold,
      fontWeight: 700,
    },
  ],
});

export default FixAndFlipTermSheet;
